import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadSimple, FileXls, X } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FileUpload({ onComplete, onCancel, hasDatasets }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (
      f &&
      (f.name.endsWith(".xlsx") ||
        f.name.endsWith(".xls") ||
        f.name.endsWith(".csv"))
    ) {
      setFile(f);
    } else {
      toast.error("Please upload an XLSX, XLS, or CSV file");
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      // The backend transparently forwards intelligent-logger CSVs (those with
      // timestamp + lat/lng + temperature columns) to the new Logger Import
      // pipeline. Show a clear success message so the operator knows where to
      // find their staged imports instead of leaving them on a blank screen.
      if (res.data?.redirected_to === "intelligent_logger_import") {
        toast.success(
          `Logger CSV recognized — ${res.data.imports_created} route(s) staged.`,
          {
            description:
              "Open Shipments → Import Logger to confirm or attach each route.",
            duration: 8000,
          }
        );
        onCancel?.(); // back out of the upload view; user navigates manually
        return;
      }
      onComplete(res.data.dataset);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-12" data-testid="file-upload-view">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-zinc-50 mb-2">
          Upload Device Data
        </h2>
        <p className="text-sm text-zinc-400">
          Upload your cold chain device events report (.xlsx, .xls, .csv)
        </p>
      </div>

      <Card
        data-testid="upload-zone"
        className={`border-2 border-dashed p-12 text-center cursor-pointer transition-colors duration-200 bg-zinc-900/50 ${
          dragActive
            ? "border-teal-400 bg-teal-400/5"
            : "border-zinc-700 hover:border-zinc-600"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleFileChange}
          data-testid="file-input"
        />

        {file ? (
          <div className="flex flex-col items-center gap-3">
            <FileXls size={48} weight="duotone" className="text-teal-400" />
            <p className="text-zinc-200 font-medium">{file.name}</p>
            <p className="text-xs text-zinc-500 font-mono">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <UploadSimple
              size={48}
              weight="duotone"
              className="text-zinc-600"
            />
            <p className="text-zinc-400">Drag & drop your file here</p>
            <p className="text-xs text-zinc-600">or click to browse</p>
          </div>
        )}
      </Card>

      <div className="flex justify-center gap-3 mt-6">
        {hasDatasets && (
          <Button
            variant="outline"
            onClick={onCancel}
            className="border-zinc-700"
            data-testid="cancel-upload-btn"
          >
            <X size={16} className="mr-2" />
            Cancel
          </Button>
        )}
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="bg-teal-500 hover:bg-teal-600 text-zinc-950 font-semibold"
          data-testid="upload-btn"
        >
          <UploadSimple size={16} className="mr-2" />
          {uploading ? "Uploading..." : "Upload & Analyze"}
        </Button>
      </div>
    </div>
  );
}
